import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import PinPad from './PinPad';

/**
 * WaiterAuthModal Component
 * Authenticates waiter before order operations
 */
export const WaiterAuthModal = ({
  isOpen,
  onClose,
  onVerify,
  waiters = [],
  loading,
  error,
  title = 'Waiter Authentication',
  description = 'Select your name and enter your PIN to continue'
}) => {
  const [selectedWaiterId, setSelectedWaiterId] = useState('');
  const [pin, setPin] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [localError, setLocalError] = useState('');
  const MAX_ATTEMPTS = 3;

  console.log('🔍 WaiterAuthModal waiters prop:', waiters, 'isOpen:', isOpen);

  // Clear errors when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setLocalError('');
      setAttempts(0);
    }
  }, [isOpen]);

  // When the parent propagates a new error, clear any stale local state so
  // the submit button is re-enabled and the inline message is fresh.
  useEffect(() => {
    if (error) {
      setLocalError('');
    }
  }, [error]);

  const handleSubmit = async (e, pinOverride) => {
    e.preventDefault();
    setLocalError('');

    // Validation
    if (!selectedWaiterId) {
      setLocalError('Please select waiter name');
      return;
    }

    const pinString = String(pinOverride || pin).trim();

    console.log('Waiter auth modal trimmedPin', pinString);

    if (pinString.length !== 4 || !/^\d{4}$/.test(pinString)) {
      setLocalError('PIN must be exactly 4 digits');
      return;
    }

    try {
      console.log('🔐 Attempting waiter verification...');
      await onVerify(selectedWaiterId, pinString);
      console.log('✅ Waiter verification successful');
      // Reset on success
      setSelectedWaiterId('');
      setPin('');
      setAttempts(0);
      setLocalError('');
    } catch (err) {
      console.error('❌ Waiter verification failed:', err);
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setLocalError(err.message || 'Authentication failed');

      if (newAttempts >= MAX_ATTEMPTS) {
        setLocalError('Maximum attempts exceeded. Please contact administrator.');
        setTimeout(() => {
          handleClose();
        }, 3000);
      }
    }
  };

  const handleClose = () => {
    setSelectedWaiterId('');
    setPin('');
    setAttempts(0);
    setLocalError('');
    onClose();
  };

  if (!isOpen) return null;

  const isLocked = attempts >= MAX_ATTEMPTS;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-96 max-w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <UserIcon className="h-6 w-6 text-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-gray-600 text-sm">{description}</p>

          {/* Waiter Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Waiter Name
            </label>
            <select
              value={selectedWaiterId}
              onChange={(e) => setSelectedWaiterId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading || isLocked}
              autoFocus
            >
              <option value="">Select waiter</option>
              {waiters.map((waiter) => (
                <option key={waiter._id} value={waiter._id}>
                  {waiter.name || waiter.username || waiter.email}
                </option>
              ))}
            </select>
          </div>

          {/* PIN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PIN
            </label>
            <PinPad value={pin} onChange={setPin} onSubmit={(p) => handleSubmit({ preventDefault: () => {} }, p)} error={localError || error} />
          </div>

          {/* Attempts indicator */}
          {attempts > 0 && !isLocked && (
            <div className="text-xs text-orange-600">
              Attempt {attempts} of {MAX_ATTEMPTS}
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || isLocked || !selectedWaiterId || pin.length !== 4}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : isLocked ? 'Locked' : 'Authenticate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WaiterAuthModal;
